/**
 * Динамическая загрузка внешнего скрипта с таймаутом и onerror.
 * Не блокирует отрисовку: если CDN недоступен — скип + лог на бэк.
 */

import { EventLogger } from '@/utils/EventLogger'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { nativeConsole } from '@/utils/consoleInterceptor'
import { isFallbackActive } from '@/utils/resolveApiUrls'

export interface LoadExternalScriptOptions {
  /** Таймаут в мс */
  timeoutMs?: number
  /** id тега — не дублируем, если уже в DOM */
  id?: string
  /** Имя для логов */
  label?: string
  /**
   * Вызывается при реальном onload скрипта (даже если таймаут уже сработал).
   * Нужен, чтобы поздняя догрузка всё равно обновила UI-флаги.
   */
  onLoad?: () => void
}

/**
 * Вставляет <script async> в head.
 * @returns true при onload до таймаута, false при ошибке/таймауте
 */
export function loadExternalScript(
  src: string,
  options: LoadExternalScriptOptions = {},
): Promise<boolean> {
  const { timeoutMs = 4000, id, label, onLoad } = options
  const name = label || src

  if (id && document.getElementById(id)) {
    onLoad?.()
    return Promise.resolve(true)
  }

  return new Promise((resolve) => {
    const startedAt = performance.now()
    const script = document.createElement('script')
    script.src = src
    script.async = true
    if (id) script.id = id

    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(ok)
    }

    const timer = setTimeout(() => {
      const ms = Math.round(performance.now() - startedAt)
      console.warn(`[loadExternalScript] timeout: ${name} (${ms}ms)`)
      EventLogger.warn('loadExternalScript', [
        `Timeout ${timeoutMs}ms: ${name}`,
        src,
      ])
      finish(false)
    }, timeoutMs)

    script.onload = () => {
      const ms = Math.round(performance.now() - startedAt)
      // nativeConsole — не вырезается esbuild drop console в prod
      nativeConsole.log(`[loadExternalScript] loaded: ${name} (${ms}ms)`)
      // Даже после таймаута — скрипт мог доехать позже
      onLoad?.()
      finish(true)
    }
    script.onerror = () => {
      const ms = Math.round(performance.now() - startedAt)
      console.warn(`[loadExternalScript] error: ${name} (${ms}ms)`)
      EventLogger.warn('loadExternalScript', [`Failed to load: ${name}`, src])
      script.remove()
      finish(false)
    }

    document.head.appendChild(script)
  })
}

/** URL внешних CDN-скриптов */
export const EXTERNAL_SCRIPTS = {
  telegramLogin: 'https://oauth.telegram.org/js/telegram-login.js?3',
  yookassa: 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js',
  ssp: 'https://ssp.sspnet.tech/sdk.js',
} as const

/** Помечает в authStore, что Telegram Login API готов (только если не на fallback-домене) */
const markTelegramLoginAvailable = (): void => {
  if (!window.Telegram?.Login || isFallbackActive.value) return
  useAuthStore().isTelegramLoginAvailable = true
}

/**
 * Фоновая загрузка при старте. Не await.
 * Вызывать после pinia (чтобы createLog и authStore работали).
 */
export function preloadExternalScripts(): void {
  // Уже в DOM / уже готов (например HMR)
  if (window.Telegram?.Login) {
    markTelegramLoginAvailable()
  }

  void loadExternalScript(EXTERNAL_SCRIPTS.telegramLogin, {
    id: 'ext-telegram-login',
    label: 'telegram-login',
    timeoutMs: 4000,
    onLoad: markTelegramLoginAvailable,
  })
  void loadExternalScript(EXTERNAL_SCRIPTS.yookassa, {
    id: 'ext-yookassa',
    label: 'yookassa',
    timeoutMs: 8000,
  })
  void loadExternalScript(EXTERNAL_SCRIPTS.ssp, {
    id: 'ext-ssp',
    label: 'ssp',
    timeoutMs: 4000,
  })
}
